import { useCallback, useEffect, useMemo, useState } from "react";
import { RIDE_STATUSES, DERIVED_RIDE_STATUSES } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #340 — a class "needs placings" only once every one of its non-scratched
// rides has reached CONFIRMED (every expected judge has confirmed marks,
// #696/#697) or further (FINAL), AND no ride in the class carries a placing
// yet. Ride state only — never the clock: a class whose scheduled time has
// passed but whose rides aren't scored isn't "waiting on placings", it's
// just running late, so it must NOT qualify here.
function classNeedsPlacings(rides) {
  if (!rides || rides.length === 0) return false;
  const scored = rides.filter((r) => r.status !== RIDE_STATUSES.SCRATCHED);
  if (scored.length === 0) return false;
  const allConfirmed = scored.every(
    (r) =>
      r.derivedStatus === DERIVED_RIDE_STATUSES.CONFIRMED ||
      r.derivedStatus === DERIVED_RIDE_STATUSES.FINAL,
  );
  if (!allConfirmed) return false;
  return rides.every((r) => r.placing == null);
}

// #340/#862 — the single source of truth for "classes needing placings",
// extracted out of ClassManager so the Classes-page chip (#340,
// data-testid=needs-placings-count) and the Placings nav badge (#862) derive
// from the exact same fetch + formula and can never disagree. Mirrors the
// original #340 fetch: one showId-scoped GET /ride call covers every class
// (mirrors the announcer's #637 running-order pattern — don't fan out a call
// per class), then one batched POST /ride/status-batch resolves each ride's
// derived status. Restricted to classes with a ring + scheduledTime, since
// #591 already hides any without those from the list the chip/badge stand in
// for.
//
// #2121 — AdminShell mounts this on the VERY FIRST authenticated screen
// alongside the show load, so the two independent reads below (the class
// list and the show's rides) run concurrently rather than one-after-another:
// neither one's result feeds the other's request, only the THIRD step
// (batchRideStatus) genuinely depends on the rides fetch, so it's the only
// one left sequential.
export function useClassesNeedingPlacings(showId) {
  const scoreApi = useScoreApi();
  const [classes, setClasses] = useState([]);
  const [ridesByClassId, setRidesByClassId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!showId) {
      setClasses([]);
      setRidesByClassId({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [classListRes, showRidesRes] = await Promise.all([
        scoreApi.classes.listClasses(showId),
        scoreApi.rides.listRides(undefined, showId),
      ]);
      const classList = unwrap(classListRes);
      const showRides = unwrap(showRidesRes);
      const ids = [...new Set(showRides.map((r) => r.id).filter(Boolean))];
      const statusRows = ids.length
        ? unwrap(await scoreApi.rides.batchRideStatus({ rideIds: ids }))
        : [];
      const statusById = Object.fromEntries(statusRows.map((r) => [r.rideId, r.status]));
      const byClass = {};
      for (const r of showRides) {
        if (!r.classId) continue;
        (byClass[r.classId] ??= []).push({ ...r, derivedStatus: statusById[r.id] ?? null });
      }
      setClasses(classList);
      setRidesByClassId(byClass);
    } catch (err) {
      setClasses([]);
      setRidesByClassId({});
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [showId, scoreApi]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const classesNeedingPlacings = useMemo(() => {
    const set = new Set();
    for (const c of classes) {
      if (!c.ring || !c.scheduledTime) continue;
      if (classNeedsPlacings(ridesByClassId[c.id])) set.add(c.id);
    }
    return set;
  }, [classes, ridesByClassId]);

  return {
    classesNeedingPlacings,
    needsPlacingsCount: classesNeedingPlacings.size,
    loading,
    error,
    refetch,
  };
}

export default useClassesNeedingPlacings;
